import React, { useContext } from 'react'
import styled from 'styled-components'
import { Draggable } from 'react-beautiful-dnd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDoubleDown, faTimes } from '@fortawesome/free-solid-svg-icons'
import Tooltip from '@bit/mui-org.material-ui.tooltip'
import { backgroundColor, lightGray, lightBlue, blue } from '../variables'
import { AdminContext } from '../containers/Admin'
import { AppContext } from '../containers/App'
import { removeIssue } from '../api'

const Container = styled.div`
  background-color: ${backgroundColor};
  border: ${props => (props.isFocused ? `3px solid ${lightBlue}` : `1px solid ${lightGray}`)}};
  border-radius: 3px;
  box-shadow: 1px 1px 3px ${lightGray};
  padding: 10px;
  position: relative;
  transition: all 0.3s;
  margin: 5px 0;

  :first-of-type {
    margin-top: 15px;
  }

  :last-of-type {
    margin-bottom: 15px;
  }

  :focus {
    outline: none;
  }
`

const Label = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;

  h4 {
    margin: 0;
    text-align: right;
  }

  svg {
    margin-left: 4px;
    transition: color 0.3s;
  }

  svg:hover {
    color: ${lightBlue};
    cursor: pointer;
  }
`

const IssueId = styled.h4`
  a {
    color: ${blue};
    cursor: pointer;
    text-decoration: none;
    transition: all 0.3s;

    :hover {
      font-size: 1.05em;
      color: ${lightBlue};
    }
  }
`

// when an element is dragged the border grows by 4px and causes overflow, hence the max-width calculation
const IssueTitle = styled.p`
  margin: 0;
  max-width: calc(100% - 4px);
  text-align: justify;
`

const Button = styled.button`
  background-color: inherit;
  cursor: pointer;
  font-size: 20px;
  border: none;
  height: 19px;
  padding: 0;
  margin: 0;

  :focus {
    outline: none;
  }

  :last-of-type {
    margin-left: 10px;
  }

  svg {
    margin: 0 auto;
    transition: all 0.3s;
  }

  :hover svg {
    color: ${lightBlue};
    transform: scale(1.1);
  }
`

const ActionButtons = styled.div`
  display: flex;
  justify-content: end;
`

function Issue(props) {
  const { allIssues, issue, isUnrankedList, handleIssueClick, isFocused, index, handleButtonClick } = props
  const issueData = allIssues.find(el => el.issueId === issue)
  const adminContext = useContext(AdminContext)
  const appContext = useContext(AppContext)

  if (!issueData) {
    return ''
  }

  return (
    <Draggable draggableId={issue} index={index}>
      {(provided, snapshot) => (
        <Container
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          isUnrankedList={isUnrankedList}
          onClick={handleIssueClick}
          data-uxd={issue}
          isFocused={isFocused}
        >
          <Label>
            <IssueId
              onClick={() => {
                window.open(`https://jira.illum.io/browse/${issue}`, '_blank')
                window.focus()
              }}
            >
              <a href={`https://jira.illum.io/browse/${issue}`} target="_blank" rel="noopener noreferrer">
                {issue}
              </a>
            </IssueId>
            {isFocused ? (
              <>
                <ActionButtons>
                  <Tooltip title="low priority; send to bottom of my ranking">
                    <Button>
                      <FontAwesomeIcon
                        icon={faAngleDoubleDown}
                        color={blue}
                        onClick={handleButtonClick}
                        data-direction="bottom"
                        data-uxd={issue}
                      />
                    </Button>
                  </Tooltip>
                  {adminContext && (
                    <Tooltip title="Remove Issue">
                      <Button>
                        <FontAwesomeIcon
                          icon={faTimes}
                          color={blue}
                          onClick={evt => {
                            removeIssue(evt.currentTarget.getAttribute('data-uxd'))
                            appContext.refetch('remove')
                          }}
                          data-direction="bottom"
                          data-uxd={issue}
                        />
                      </Button>
                    </Tooltip>
                  )}
                </ActionButtons>
              </>
            ) : null}
          </Label>
          <IssueTitle>{issueData.title}</IssueTitle>
        </Container>
      )}
    </Draggable>
  )
}

export default Issue
